import { logger } from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, PhoneIcon, MailIcon, BuildingIcon, DownloadIcon, FileSpreadsheetIcon, ContactIcon, CalendarIcon, UserIcon, ArchiveIcon, PowerIcon, RotateCcwIcon, FilterIcon, UsersIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Contact, ContactStatus, ContactSource } from '../../types';
import { exportContactsToExcel } from '../../utils/excelExport';
import { exportContactsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Contacts() {
  const {
    contacts,
    addContact,
    updateContact,
    deleteContact
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('contacts');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // View Mode State: 'active', 'archived', 'deactivated'
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  // Modals for actions
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false); // Only for permanent delete if needed, or admin override
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    position: '',
    contactStatus: 'nuevo' as ContactStatus,
    contactSource: 'otro' as ContactSource,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Load statuses from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-contacts-status');
      if (saved) {
        setItemStatuses(JSON.parse(saved));
      }
    } catch (e) {
      logger.error('Error loading contact statuses', e);
    }
  }, []);
  // Update status helper
  const updateItemStatus = (id: string, status: 'archived' | 'deactivated' | null) => {
    const newStatuses = {
      ...itemStatuses
    };
    if (status === null) {
      delete newStatuses[id]; // Restore to active
    } else {
      newStatuses[id] = status;
    }
    setItemStatuses(newStatuses);
    localStorage.setItem('savidna-contacts-status', JSON.stringify(newStatuses));
  };
  // Filter contacts based on viewMode and search
  const filteredContacts = contacts.filter(c => {
    // Status filter
    const status = itemStatuses[c.id];
    const matchesStatus = viewMode === 'active' ? !status : status === viewMode;
    if (!matchesStatus) return false;
    // Search filter
    return c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.email?.toLowerCase().includes(searchTerm.toLowerCase()) || (c.contactType || '').toLowerCase().includes(searchTerm.toLowerCase());
  });
  const {
    paginatedItems: paginatedContacts,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredContacts);
  const openNewForm = () => {
    setSelectedContact(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      company: '',
      position: '',
      contactStatus: 'nuevo',
      contactSource: 'otro',
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      company: contact.company || '',
      position: contact.position || '',
      contactStatus: contact.contactStatus || 'nuevo',
      contactSource: contact.contactSource || 'otro',
      notes: contact.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Action handlers
  const handleArchive = () => {
    if (selectedContact) {
      updateItemStatus(selectedContact.id, 'archived');
      setIsArchiveConfirmOpen(false);
      setSelectedContact(null);
    }
  };
  const handleDeactivate = () => {
    if (selectedContact) {
      updateItemStatus(selectedContact.id, 'deactivated');
      setIsDeactivateConfirmOpen(false);
      setSelectedContact(null);
    }
  };
  const handleRestore = (contact: Contact) => {
    updateItemStatus(contact.id, null);
  };
  // Admin only - permanent delete
  const handleDelete = () => {
    if (selectedContact) {
      const name = selectedContact.name;
      deleteContact(selectedContact.id);
      // Also clean up status if exists
      const newStatuses = {
        ...itemStatuses
      };
      delete newStatuses[selectedContact.id];
      setItemStatuses(newStatuses);
      localStorage.setItem('savidna-contacts-status', JSON.stringify(newStatuses));
      showToast({
        type: 'error',
        title: 'Contacto eliminado',
        message: `${name} fue eliminado permanentemente.`
      });
      setIsDeleteConfirmOpen(false);
      setSelectedContact(null);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.email) errors.email = 'El correo es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedContact) {
      updateContact(selectedContact.id, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        company: formData.company,
        position: formData.position,
        contactStatus: formData.contactStatus,
        contactSource: formData.contactSource,
        notes: formData.notes
      });
      showToast({
        type: 'success',
        title: 'Contacto actualizado',
        message: `${formData.name} fue actualizado correctamente.`
      });
    } else {
      addContact({
        ...formData,
        createdBy: user?.name || 'Sistema'
      });
      showToast({
        type: 'success',
        title: 'Contacto creado',
        message: `${formData.name} fue registrado correctamente.`
      });
    }
    setIsFormOpen(false);
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = contacts;
    if (dateFrom && dateTo) {
      dataToExport = contacts.filter(c => {
        const dateStr = new Date(c.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportContactsToExcel(dataToExport);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = contacts;
    if (dateFrom && dateTo) {
      dataToExport = contacts.filter(c => {
        const dateStr = new Date(c.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportContactsToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateContact(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving contact in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateContact(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating contact in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateContact(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring contact in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  return <div className="space-y-6" data-id="element-2188">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-lg text-white" data-id="element-2189">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-2190" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-2191" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-2192" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-2193">
          <div data-id="element-2194">
            <div className="flex items-center gap-2 mb-1" data-id="element-2195">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-2196"><UsersIcon className="w-6 h-6 text-white" data-id="element-2197" /></div>
              <h1 className="text-2xl font-bold" data-id="element-2198">Contactos</h1>
            </div>
            <p className="text-white/80 text-sm font-medium" data-id="element-2199">
              Gestión de relaciones y agenda de contactos
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-2200">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-2201">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2203" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2202">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2205" />} onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2204">
                Exportar Excel
              </Button>}
            {canCreate('contacts') && <Button variant="outline" onClick={openNewForm} className="text-white border-transparent shadow-lg" style={{
            backgroundColor: "#1aa2b2"
          }} onMouseEnter={e => e.currentTarget.style.backgroundColor = "#178fa0"} onMouseLeave={e => e.currentTarget.style.backgroundColor = "#1aa2b2"} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2207" />} data-id="element-2206">
                Nuevo Contacto
              </Button>}
          </div>
        </div>

        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2208" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-2209" />
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-2210">
          <LockIcon className="w-4 h-4" data-id="element-2211" />
          <span data-id="element-2212">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            contactos.
          </span>
        </div>}

      {/* View Tabs & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center" data-id="element-2213">
        <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-xl" data-id="element-2214">
          <button onClick={() => setViewMode('active')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'active' ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2215">
            Activos
          </button>
          <button onClick={() => setViewMode('archived')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'archived' ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2216">
            Archivados
          </button>
          <button onClick={() => setViewMode('deactivated')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewMode === 'deactivated' ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2217">
            Desactivados
          </button>
        </div>

        <div className="relative w-full md:w-64" data-id="element-2218">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-2219" />
          <Input style={{
          paddingLeft: "2.5rem"
        }} placeholder="Buscar contactos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-2220" />
        </div>
      </div>

      {/* Contacts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-id="element-2221">
        {paginatedContacts.length === 0 ? <div className="col-span-full py-16 text-center" data-id="element-2222">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2223">
              <ContactIcon className="w-8 h-8 text-gray-400" data-id="element-2224" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-2225">
              No hay contactos{' '}
              {viewMode === 'active' ? '' : viewMode === 'archived' ? 'archivados' : 'desactivados'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-2226">
              {searchTerm ? 'Intenta con otra búsqueda' : 'Comienza agregando un nuevo contacto'}
            </p>
          </div> : paginatedContacts.map(contact => <Card key={contact.id} padding="sm" className="group hover:shadow-md transition-all duration-200 border-l-4 border-l-teal-500" data-id="element-2227">
              <div className="flex items-start justify-between mb-2" data-id="element-2228">
                <div className="flex items-center gap-2" data-id="element-2229">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm shadow-sm shadow-teal-500/20 flex-shrink-0" data-id="element-2230">
                    {contact.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0" data-id="element-2231">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate" data-id="element-2232">
                      {contact.name}
                    </h3>
                    <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full ${contact.contactStatus === 'nuevo' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : contact.contactStatus === 'contactado' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' : contact.contactStatus === 'interesado' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : contact.contactStatus === 'cita_agendada' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : contact.contactStatus === 'paciente' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : contact.contactStatus === 'perdido' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-2233">
                      {contact.contactStatus === 'nuevo' ? 'Nuevo' : contact.contactStatus === 'contactado' ? 'Contactado' : contact.contactStatus === 'interesado' ? 'Interesado' : contact.contactStatus === 'cita_agendada' ? 'Cita Agendada' : contact.contactStatus === 'paciente' ? 'Paciente' : contact.contactStatus === 'perdido' ? 'Perdido' : 'Sin estado'}
                    </span>
                  </div>
                </div>

                {/* Actions Menu */}
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2234">
                  {viewMode === 'active' ? <>
                      {canEdit('contacts') && <button onClick={() => openEditForm(contact)} className="p-1 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-md transition-colors" title="Editar" data-id="element-2235">
                          <EditIcon className="w-3.5 h-3.5" data-id="element-2236" />
                        </button>}
                      {canDelete('contacts') && <>
                          <button onClick={() => {
                  setSelectedContact(contact);
                  setIsArchiveConfirmOpen(true);
                }} className="p-1 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-md transition-colors" title="Archivar" data-id="element-2237">
                            <ArchiveIcon className="w-3.5 h-3.5" data-id="element-2238" />
                          </button>
                          <button onClick={() => {
                  setSelectedContact(contact);
                  setIsDeactivateConfirmOpen(true);
                }} className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors" title="Desactivar" data-id="element-2239">
                            <PowerIcon className="w-3.5 h-3.5" data-id="element-2240" />
                          </button>
                        </>}
                    </> : canDelete('contacts') && <button onClick={() => handleRestore(contact)} className="p-1 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-md transition-colors" title="Restaurar" data-id="element-2241">
                        <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-2242" />
                      </button>}
                </div>
              </div>

              <div className="space-y-1 mb-2 text-xs text-gray-600 dark:text-gray-300" data-id="element-2243">
                <div className="flex items-center gap-1.5" data-id="element-2244">
                  <ContactIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-2245" />
                  <span className="truncate" data-id="element-2246">
                    Origen:{' '}
                    {contact.contactSource === 'whatsapp' ? 'WhatsApp' : contact.contactSource === 'instagram' ? 'Instagram' : contact.contactSource === 'pagina_web' ? 'Página Web' : contact.contactSource === 'google' ? 'Google' : contact.contactSource === 'referido' ? 'Referido' : 'Otro'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-2247">
                  <MailIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-2248" />
                  <span className="truncate" data-id="element-2249">{contact.email}</span>
                </div>
                {contact.phone && <div className="flex items-center gap-1.5" data-id="element-2250">
                    <PhoneIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-2251" />
                    <span data-id="element-2252">{contact.phone}</span>
                  </div>}
              </div>

              {contact.notes && contact.notes.trim() && <div className="bg-yellow-50 dark:bg-yellow-900/10 px-2 py-1.5 rounded-md border border-yellow-100 dark:border-yellow-900/20 mb-2" data-id="element-2253">
                  <p className="text-[11px] text-gray-600 dark:text-gray-400 italic line-clamp-2" data-id="element-2254">
                    "{contact.notes}"
                  </p>
                </div>}

              {/* Footer info */}
              <div className="border-t border-gray-100 dark:border-gray-700 pt-2 flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500" data-id="element-2255">
                <div className="flex items-center gap-1" data-id="element-2256">
                  <CalendarIcon className="w-3 h-3" data-id="element-2257" />
                  <span data-id="element-2258">
                    {new Date(contact.createdAt).toLocaleDateString('es-ES', {
                month: 'short',
                day: 'numeric'
              })}
                  </span>
                </div>
                {contact.createdBy && <div className="flex items-center gap-1" data-id="element-2259">
                    <UserIcon className="w-3 h-3" data-id="element-2260" />
                    <span className="truncate max-w-[80px]" data-id="element-2261">
                      {contact.createdBy}
                    </span>
                  </div>}
              </div>
            </Card>)}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-2262" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedContact ? 'Editar Contacto' : 'Nuevo Contacto'} size="md" icon={<ContactIcon className="w-5 h-5" data-id="element-2264" />} accentColor="teal" data-id="element-2263">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2265">
          {/* Personal Info */}
          <div data-id="element-2266">
            <div className="flex items-center gap-2 mb-4" data-id="element-2267">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-2268"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2269">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2270">
              <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Nombre del contacto" data-id="element-2271" />
              <div className="grid grid-cols-2 gap-4" data-id="element-2272">
                <Select label="Estado del Contacto" options={[{
                value: 'nuevo',
                label: 'Nuevo'
              }, {
                value: 'contactado',
                label: 'Contactado'
              }, {
                value: 'interesado',
                label: 'Interesado'
              }, {
                value: 'cita_agendada',
                label: 'Cita Agendada'
              }, {
                value: 'paciente',
                label: 'Paciente'
              }, {
                value: 'perdido',
                label: 'Perdido'
              }]} value={formData.contactStatus} onChange={value => setFormData({
                ...formData,
                contactStatus: value as ContactStatus
              })} data-id="element-2273" />
                <Select label="Origen del Contacto" options={[{
                value: 'whatsapp',
                label: 'WhatsApp'
              }, {
                value: 'instagram',
                label: 'Instagram'
              }, {
                value: 'pagina_web',
                label: 'Página Web'
              }, {
                value: 'google',
                label: 'Google'
              }, {
                value: 'referido',
                label: 'Referido'
              }, {
                value: 'otro',
                label: 'Otro'
              }]} value={formData.contactSource} onChange={value => setFormData({
                ...formData,
                contactSource: value as ContactSource
              })} data-id="element-2274" />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div data-id="element-2275">
            <div className="flex items-center gap-2 mb-4" data-id="element-2276">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-2277"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2278">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2279">
              <div className="grid grid-cols-2 gap-4" data-id="element-2280">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="809-000-0000" data-id="element-2281" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="contacto@email.com" data-id="element-2282" />
              </div>
              <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} rows={3} placeholder="Notas adicionales sobre el contacto..." data-id="element-2283" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2284">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-2285">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-2286">
              {selectedContact ? 'Guardar Cambios' : 'Crear Contacto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Archive Confirmation */}
      <Modal isOpen={isArchiveConfirmOpen} onClose={() => setIsArchiveConfirmOpen(false)} title="Archivar Contacto" size="sm" data-id="element-2287">
        <div className="space-y-4" data-id="element-2288">
          <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg flex items-start gap-3" data-id="element-2289">
            <ArchiveIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" data-id="element-2290" />
            <div data-id="element-2291">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-2292">
                ¿Archivar contacto?
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1" data-id="element-2293">
                El contacto "{selectedContact?.name}" se moverá a la lista de
                archivados. Podrás restaurarlo después.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3" data-id="element-2294">
            <Button variant="outline" onClick={() => setIsArchiveConfirmOpen(false)} data-id="element-2295">
              Cancelar
            </Button>
            <Button onClick={handleArchive} className="bg-amber-500 hover:bg-amber-600 text-white" data-id="element-2296">
              Archivar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Confirmation */}
      <Modal isOpen={isDeactivateConfirmOpen} onClose={() => setIsDeactivateConfirmOpen(false)} title="Desactivar Contacto" size="sm" data-id="element-2297">
        <div className="space-y-4" data-id="element-2298">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg flex items-start gap-3" data-id="element-2299">
            <PowerIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" data-id="element-2300" />
            <div data-id="element-2301">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-300" data-id="element-2302">
                ¿Desactivar contacto?
              </h4>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1" data-id="element-2303">
                El contacto "{selectedContact?.name}" se marcará como inactivo.
                No aparecerá en las listas principales.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3" data-id="element-2304">
            <Button variant="outline" onClick={() => setIsDeactivateConfirmOpen(false)} data-id="element-2305">
              Cancelar
            </Button>
            <Button onClick={handleDeactivate} variant="danger" data-id="element-2306">
              Desactivar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Permanent Delete Confirmation (Admin Only) */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Eliminar Permanentemente" size="sm" data-id="element-2307">
        <div className="space-y-4" data-id="element-2308">
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-100 dark:border-red-800" data-id="element-2309">
            <p className="text-red-800 dark:text-red-300 font-medium" data-id="element-2310">
              Esta acción es irreversible
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-2311">
              ¿Está seguro de que desea eliminar permanentemente a "
              {selectedContact?.name}"?
            </p>
          </div>
          <div className="flex justify-end gap-3" data-id="element-2312">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-2313">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-2314">
              Eliminar Definitivamente
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Contactos" isExporting={isExporting} data-id="element-2315" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Contactos a PDF" isExporting={isPdfExporting} data-id="element-2316" />
    </div>;
}